<template>
  <v-modal
    id="show-case-update-wizard-modal"
    class-name="modal-fullscreen-sm-down w-auto"
    size="xl"
    @closed="$emit('closed')"
  >
    <modal-header>
      <modal-title>{{ $t("template.editCaseWithWizard") }}</modal-title>
    </modal-header>

    <modal-body class="pb-0">
      <div class="wizard-container">
        <!-- Stepper -->
        <div class="wizard-stepper mb-24">
          <div class="wizard-stepper-track">
            <div class="wizard-stepper-fill" :style="{ width: progressLine + '%' }"></div>
          </div>
          <div class="d-flex justify-content-between position-relative">
            <div v-for="n in totalSteps" :key="n" class="wizard-step-item text-center">
              <div
                class="wizard-step-bubble"
                :class="{
                  'is-active': step === n,
                  'is-done': step > n,
                }"
              >
                <span v-if="step > n" class="mi mi-check" />
                <span v-else>{{ n }}</span>
              </div>
              <div class="wizard-step-label" :class="{ 'fw-bold text-dark': step === n }">
                {{ getStepName(n) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Step body -->
        <div class="wizard-body">
              <!-- Step 1: Dates & Services -->
              <div v-if="step === 1" class="row mb-4 mt-4">
                <div class="col-12">
                  <div class="wizard-step-card">
                    <fieldset>
                      <div class="form-outline mb-4">
                        <label class="label">
                          <span>{{ $t("clientTour.products") }}*</span>
                        </label>
                        <div class="form-control-plaintext border rounded px-3 py-2 bg-light">
                          {{ template?.name || "—" }}
                        </div>
                      </div>
                    </fieldset>
                    <div class="row g-3">
                      <div class="col-12 col-md-6">
                        <div id="case-update-init-date" class="form-outline">
                          <label class="label" :class="{ disabled: !template?.id }">
                            <span>{{ $t("general.shared.startDate") }}*</span>
                          </label>
                          <Datepicker
                            v-model="initDate"
                            :enable-time-picker="false"
                            :disabled="!template?.id"
                            :format="settings.formatDate"
                            :highlight="highlightedDates"
                            :input-class-name="`form-control ${!!errors.initDate ? 'is-invalid' : ''}`"
                            :min-date="
                              minDate instanceof Date ? minDate : minDate.toJSDate ? minDate.toJSDate() : new Date()
                            "
                            :month-change-on-scroll="false"
                            auto-apply
                            calendar-cell-class-name="init-date-cell"
                            class="form-datepicker"
                          />
                          <span v-if="!!errors.initDate" class="invalid-feedback d-block">
                            {{ errors.initDate }}
                          </span>
                        </div>
                      </div>
                      <div class="col-12 col-md-6">
                        <div id="case-update-alt-date" class="form-outline">
                          <label class="label" :class="{ disabled: !template?.id }">
                            <span>{{ $t("general.shared.alternativeStartDate") }}</span>
                          </label>
                          <Datepicker
                            v-model="alternativeStartDate"
                            :enable-time-picker="false"
                            :format="settings.formatDate"
                            :highlight="highlightedDates"
                            :disabled="!template?.id"
                            :input-class-name="`form-control ${!!errors.alternativeStartDate ? 'is-invalid' : ''}`"
                            :min-date="incrementDateByOneDay(initDate)"
                            :month-change-on-scroll="false"
                            auto-apply
                            calendar-cell-class-name="init-date-cell"
                            class="form-datepicker"
                          />
                        </div>
                      </div>
                    </div>
                    <div v-if="template?.id" class="wizard-template-meta mt-16 mb-12">
                      <div class="wizard-meta-chip">
                        <span class="mi mi-directions-car me-4" style="font-size: 14px" />
                        {{ $t("clientTour.travelType") }}:
                        <strong class="ms-4">{{ template.travelType }}</strong>
                      </div>
                      <div class="wizard-meta-chip">
                        <span class="mi mi-calendar-today me-4" style="font-size: 14px" />
                        {{ $t("template.days") }}:
                        <strong class="ms-4">{{ template.activityDaysCount }}</strong>
                      </div>
                    </div>
                    <div class="d-flex flex-wrap gap-12">
                      <label
                        v-if="template?.hasTransfer"
                        class="wizard-option-toggle"
                        :class="{
                          'is-checked': transferCheck,
                          'wizard-option-toggle--disabled': !template?.id,
                        }"
                      >
                        <input type="checkbox" class="d-none" v-model="transferCheck" :disabled="!template?.id" />
                        <v-icon class="icon-transfer mx-6" />
                        {{ $t("supplier.transfer") }}
                        <span class="wizard-toggle-check ms-8"
                          ><span v-if="transferCheck" class="mi mi-check" style="font-size: 10px"
                        /></span>
                      </label>
                      <label
                        v-if="template?.hasLuggage"
                        class="wizard-option-toggle"
                        :class="{
                          'is-checked': transportCheck,
                          'wizard-option-toggle--disabled': !template?.id,
                        }"
                      >
                        <input type="checkbox" class="d-none" v-model="transportCheck" :disabled="!template?.id" />
                        <v-icon class="icon-luggage-cart mx-6" />
                        {{ $t("supplier.luggageTransport") }}
                        <span class="wizard-toggle-check ms-8"
                          ><span v-if="transportCheck" class="mi mi-check" style="font-size: 10px"
                        /></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 2: Rooming -->
              <div v-if="step === 2" class="row mb-4 mt-8">
                <div class="col-12">
                  <h6 class="wizard-step-section-title">
                    {{ $t("template.caseWizardTotalPax", { n: paxFields.length }) }}
                  </h6>
                </div>
                <div class="col-12">
                  <div
                    class="wizard-rooming-panel search-separator-container gap-20 mt-12"
                    :class="{ 'scrollable-padding': rooming.length > 2 }"
                  >
                    <div class="wizard-rooming-toolbar">
                      <span
                        class="wizard-rooming-toolbar-label separator-text text-sm"
                        :class="{ 'span-disabled': !template?.id }"
                      >
                        {{ $t("clientTour.rooming") }}*
                      </span>
                      <v-button
                        class="btn-icon p-1 rounded-circle"
                        @click.stop
                        size="sm"
                        variant="outline-dark"
                        @click="toggleRooming"
                      >
                        <template v-if="isCreateRoom">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            version="1.1"
                            width="256"
                            height="256"
                            viewBox="0 0 256 256"
                            xml:space="preserve"
                          >
                            <defs></defs>
                            <g
                              class="icon-svg"
                              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
                            >
                              <path
                                d="M 86.5 48.5 h -83 C 1.567 48.5 0 46.933 0 45 s 1.567 -3.5 3.5 -3.5 h 83 c 1.933 0 3.5 1.567 3.5 3.5 S 88.433 48.5 86.5 48.5 z"
                                class=""
                                transform=" matrix(1 0 0 1 0 0) "
                                stroke-linecap="round"
                              />
                              <path
                                d="M 86.5 48.5 h -83 C 1.567 48.5 0 46.933 0 45 s 1.567 -3.5 3.5 -3.5 h 83 c 1.933 0 3.5 1.567 3.5 3.5 S 88.433 48.5 86.5 48.5 z"
                                class="icon-svg-minus"
                                transform=" matrix(1 0 0 1 0 0) "
                                stroke-linecap="round"
                              />
                            </g>
                          </svg>
                        </template>
                        <template v-else>
                          <v-icon icon="plus" />
                        </template>
                      </v-button>
                    </div>
                    <template v-if="isCreateRoom">
                      <div class="wizard-rooming-inner">
                        <div
                          class="scrollable-rooming"
                          :class="{ 'wizard-rooming-scroll': rooming.length > 2 }"
                          :style="{
                            maxHeight: rooming.length > 2 ? '300px' : 'auto',
                            overflowY: rooming.length > 2 ? 'auto' : 'visible',
                          }"
                        >
                          <div class="heading"></div>
                          <div v-for="(room, index) in rooming" :key="index">
                            <div class="Participants-wrapper position-relative d-md-flex gap-20">
                              <div class="form-outline w-100">
                                <div class="open-question-icon" v-if="othersType">
                                  <div
                                    class="others-type-tooltip-description"
                                    :data-title="$t('general.toast.otherTypeTooltipMessage')"
                                  >
                                    <v-icon icon="question" size="12" style="opacity: 0.5" />
                                    <div class="tooltip-right-arrow"></div>
                                  </div>
                                </div>
                                <label class="label" :class="{ disabled: !template?.id }">
                                  <span v-if="!othersType || room.type?.value === 'other_room'">Room Type</span>
                                </label>
                                <v-select
                                  v-if="!othersType || room.type?.value === 'other_room'"
                                  form-type="outline"
                                  :clearable="false"
                                  :disabled="!template?.id"
                                  :options="
                                    roomTypes.map((roomType) => ({ value: roomType.value, label: $t(roomType.label) }))
                                  "
                                  style="min-width: 235px; padding: 0px 0px"
                                  v-model="room.type"
                                  @input="(newValue: number) => handleRoomingPaxInput(room, newValue)"
                                  @change="handleRoomChange(room)"
                                >
                                  <template v-slot:no-options>
                                    <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                                  </template>
                                </v-select>
                              </div>
                              <div v-if="othersType == false" class="form-outline mx-2">
                                <label class="label" :class="{ disabled: !template?.id }">
                                  <span>Quantity</span>
                                </label>
                                <v-form-input
                                  class="quantity"
                                  type="number"
                                  form-type="outline"
                                  autocomplete="off"
                                  :placeholder="$t('general.shared.quantityShort')"
                                  id="qty-input"
                                  :disabled="!template?.id"
                                  style="min-width: 110px; max-width: 150px"
                                  v-model="room.quantity"
                                  @input="(newValue: number, event: any) => { handleRoomingPaxInput(room, newValue); handleRoomChange(room); handleInput(event, room, 'quantity') }"
                                  @change="handleRoomChange(room)"
                                  @keydown="(event: any) => handleKeydown(event, room, 'quantity')"
                                  @wheel.prevent
                                />
                              </div>
                              <div v-if="othersType == false" class="form-outline">
                                <label class="label" :class="{ disabled: !template?.id }">
                                  <span>{{ $t("general.shared.pax") }}</span>
                                </label>
                                <v-form-input
                                  type="number"
                                  autocomplete="off"
                                  form-type="outline"
                                  :placeholder="$t('general.shared.pax')"
                                  id="roomingPaxInput"
                                  :disabled="!template?.id"
                                  style="min-width: 80px; max-width: 150px"
                                  class="ml-4 mr-4 pax"
                                  v-model="room.pax"
                                  @input="(newValue: number) => handleRoomingPaxInput(room, newValue)"
                                  @wheel.prevent
                                />
                              </div>
                              <div
                                class="remove-trash-icon"
                                v-if="!othersType || room.type?.value === 'other_room'"
                                v-show="rooming.length > 1"
                              >
                                <v-icon
                                  @click="removeRoomingEntry(Number(index))"
                                  icon="trash"
                                  size="12"
                                  class="removePaxEntry"
                                />
                              </div>
                            </div>
                            <div class="error-message" v-if="room.error && !othersType">{{ room.error }}</div>
                          </div>

                          <v-button
                            v-if="othersType == false || rooming.length === 0"
                            class="btn-icon p-1 rounded-circle add-btn"
                            variant="outline-dark"
                            style="margin-left: 10px"
                            size="sm"
                            @click="addRoomingEntry"
                          >
                            <template v-slot:default>
                              <v-icon icon="plus" />
                            </template>
                          </v-button>
                          <div class="clear"></div>
                          <div class="flex-row total-wrapper total-section" v-if="othersType == false">
                            <label>Total:</label>
                            <v-form-input
                              id="totalRoomingPax"
                              :type="'text'"
                              style="width: 25%; margin-left: 8px"
                              v-model="totalRoomingPax"
                              class="custom"
                              :disabled="true"
                            />
                          </div>
                          <div v-if="showError && othersType == false && step >= 3" class="error-message">
                            Total rooming capacity does not match the no of participants.
                          </div>
                        </div>
                        <div v-if="othersType == true" class="mt-16">
                          <v-html-editor
                            v-model="other_room_requirements"
                            id="collaborator-other-room-requirements"
                            ref="collaborator-other-room-requirements"
                            :height="'300px'"
                          />
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <!-- Step 3: Client & Pax -->
              <div v-if="step === 3" class="row mb-4 mt-4">
                <fieldset>
                  <div class="row mb-4">
                    <div class="col-12">
                      <label class="label" :class="{ disabled: !template?.id }">
                        <span>{{ $t("clientTour.client") }}</span>
                      </label>
                      <div class="form-control-plaintext border rounded px-3 py-2 bg-light">
                        {{ clientNameDisplay }}
                      </div>
                    </div>
                  </div>

                  <div
                    class="wizard-pax-panel search-separator-container mt-16"
                    :class="{
                      'wizard-pax-panel--scroll': paxFields.length > 4,
                      'scrollable-padding': paxFields.length > 4,
                    }"
                    :style="{
                      maxHeight: paxFields.length > 4 ? '450px' : 'auto',
                      overflowY: paxFields.length > 4 ? 'auto' : 'visible',
                      overflowX: paxFields.length > 4 ? 'hidden' : 'hidden',
                    }"
                  >
                    <div class="wizard-pax-toolbar">
                      <span
                        class="wizard-pax-toolbar-label separator-text text-sm"
                        :class="{ 'span-disabled': !template?.id }"
                      >
                        {{ $t("general.shared.pax") }} *
                      </span>
                      <v-button
                        class="btn-icon p-1 rounded-circle"
                        @click.stop
                        size="sm"
                        variant="outline-dark"
                        @click="togglePaxField"
                      >
                        <template v-if="isPax">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            version="1.1"
                            width="256"
                            height="256"
                            viewBox="0 0 256 256"
                            xml:space="preserve"
                          >
                            <defs></defs>
                            <g
                              class="icon-svg"
                              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
                            >
                              <path
                                d="M 86.5 48.5 h -83 C 1.567 48.5 0 46.933 0 45 s 1.567 -3.5 3.5 -3.5 h 83 c 1.933 0 3.5 1.567 3.5 3.5 S 88.433 48.5 86.5 48.5 z"
                                class=""
                                transform=" matrix(1 0 0 1 0 0) "
                                stroke-linecap="round"
                              />
                              <path
                                d="M 86.5 48.5 h -83 C 1.567 48.5 0 46.933 0 45 s 1.567 -3.5 3.5 -3.5 h 83 c 1.933 0 3.5 1.567 3.5 3.5 S 88.433 48.5 86.5 48.5 z"
                                class="icon-svg-minus"
                                transform=" matrix(1 0 0 1 0 0) "
                                stroke-linecap="round"
                              />
                            </g>
                          </svg>
                        </template>
                        <template v-else>
                          <VIcon icon="plus" />
                        </template>
                      </v-button>
                    </div>
                    <template v-if="isPax">
                      <div class="wizard-pax-list">
                        <div v-for="(pax, index) in paxFields" :key="index" class="wizard-pax-row">
                          <div class="rounded-index-circle wizard-pax-badge" aria-hidden="true">
                            {{ index + 1 }}
                          </div>
                          <div class="wizard-pax-fields">
                            <div class="wizard-pax-field form-outline">
                              <label class="label" :class="{ disabled: !template?.id }">
                                <span>{{ $t("general.shared.name") }}</span>
                              </label>
                              <v-form-input
                                v-model="pax.paxName"
                                form-type="outline"
                                autocomplete="off"
                                :placeholder="$t('general.shared.name')"
                                :id="index === 0 ? 'pax-name-input' : `pax-name-input-${index}`"
                                :disabled="!template?.id"
                              />
                            </div>
                            <div class="wizard-pax-field form-outline">
                              <label class="label" :class="{ disabled: !template?.id }">
                                <span>{{ $t("general.shared.email") }}</span>
                              </label>
                              <v-form-input
                                v-model="pax.paxEmail"
                                form-type="outline"
                                autocomplete="off"
                                type="email"
                                :placeholder="$t('general.shared.email')"
                                :id="`pax-email-input-${index}`"
                                :disabled="!template?.id"
                              />
                            </div>
                            <div class="wizard-pax-field form-outline wizard-pax-room-field">
                              <label class="label" :class="{ disabled: !template?.id || !isCreateRoom }">
                                <span>{{ $t("clientTour.rooming") }}</span>
                              </label>
                              <v-select
                                v-model="pax.roomType"
                                :clearable="false"
                                :disabled="!template?.id || !isCreateRoom"
                                :options="paxRoomTypeOptions(index)"
                                :placeholder="$t('clientTour.rooming')"
                                style="min-width: 180px"
                              >
                                <template v-slot:no-options>
                                  <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                                </template>
                              </v-select>
                            </div>
                          </div>
                          <div v-if="paxFields.length > 1" class="wizard-pax-row-remove">
                            <button
                              type="button"
                              class="wizard-pax-remove-btn"
                              :aria-label="$t('general.shared.remove')"
                              @click="removePaxEntry(index)"
                            >
                              <v-icon icon="trash" size="14" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div class="wizard-pax-add-row">
                        <v-button class="wizard-pax-add-btn" variant="outline-dark" size="sm" @click="addPaxEntry">
                          <v-icon icon="plus" class="me-6" />
                          {{ $t("general.button.add") }}
                        </v-button>
                      </div>
                    </template>
                  </div>
                </fieldset>
              </div>

              <!-- Step 4: Hotels -->
              <div v-if="step === 4" class="row mt-4">
                <div class="col-12">
                  <h6 class="wizard-step-section-title mb-12">
                    {{ $t("general.button.select") }} {{ $t("general.shared.hotels") }}
                  </h6>
                  <div class="hotel-table">
                  <!-- Header -->
                  <div class="hotel-row hotel-header">
                    <div class="hotel-cell index-col"></div>
                    <div class="hotel-cell date-col">{{ $t("general.shared.day") }}</div>
                    <div class="hotel-cell name-col">{{ $t("general.shared.hotelName") }}</div>
                    <div class="hotel-cell action-col"></div>
                    <div class="hotel-cell accordion-col"></div>
                  </div>

                  <div class="accordion" id="hotelAccordion">
                    <div v-for="(row, idx) in hotelRows" :key="idx" class="hotel-data-row mb-8 accordion-item">
                      <!-- Rows -->
                      <div class="hotel-row accordion-toggle custom-cursor">
                        <!-- Index -->
                        <div
                          class="hotel-cell index-col"
                          :aria-controls="`hotel-room-${idx}`"
                          :data-bs-target="`#hotel-room-${idx}`"
                          aria-expanded="false"
                          data-bs-toggle="collapse"
                          @click="toggleAccordion(idx)"
                        >
                          <div class="rounded-index-circle d-flex align-items-center justify-content-center">
                            <div>{{ idx + 1 }}</div>
                          </div>
                        </div>

                        <!-- Date -->
                        <div
                          class="hotel-cell date-col"
                          :aria-controls="`hotel-room-${idx}`"
                          :data-bs-target="`#hotel-room-${idx}`"
                          aria-expanded="false"
                          data-bs-toggle="collapse"
                          @click="toggleAccordion(idx)"
                        >
                          {{ row.date }}
                        </div>

                        <!-- Hotel Name -->
                        <div class="hotel-cell name-col">
                          <v-select
                            v-model="row.selectedHotel"
                            :options="row.hotelOptions"
                            label="name"
                            :clearable="false"
                            style="width: 100%"
                            @update:modelValue="onHotelChange(row, $event)"
                          >
                            <template v-slot:option="option">
                              <span>{{ (option as any).hotel_name }}</span>
                            </template>
                            <template v-slot:selected-option="option">
                              <span>{{ (option as any).hotel_name }}</span>
                            </template>
                          </v-select>
                        </div>

                        <!-- Actions -->
                        <div class="hotel-cell action-col">
                          <div class="d-flex gap-8" v-if="row.hotelOptions.length > 0 && row.selectedHotel">
                            <!-- Booking -->
                            <div
                              class="d-flex align-items-center justify-content-center stepper-circle completed custom-cursor"
                              @click="
                                generateBookingURL(
                                  row.selectedHotel.hotel_name,
                                  row.date,
                                  row.checkoutDate,
                                  paxFields.length,
                                  0,
                                  totalQuantity
                                )
                              "
                            >
                              <v-icon size="lg" icon="booking" class="booking-icon" />
                            </div>

                            <!-- Website -->
                            <div
                              class="d-flex align-items-center justify-content-center stepper-circle active custom-cursor"
                              @click="openWebsite(row.selectedHotel.website)"
                            >
                              <v-icon size="lg" icon="sphere" class="website-icon" />
                            </div>
                          </div>
                        </div>

                        <!-- Accordion Toggle -->
                        <div
                          class="hotel-cell accordion-col custom-cursor"
                          :aria-controls="`hotel-room-${idx}`"
                          :data-bs-target="`#hotel-room-${idx}`"
                          aria-expanded="false"
                          data-bs-toggle="collapse"
                          @click="toggleAccordion(idx)"
                        >
                          <v-icon
                            class="accordion-icon"
                            :icon="openIndex === idx ? 'chevron-down' : 'chevron-up'"
                            size="xl"
                          />
                        </div>
                      </div>

                      <!-- Accordion Content -->
                      <div
                        :id="`hotel-room-${idx}`"
                        class="hotel-accordion-row collapse"
                        data-bs-parent="#hotelAccordion"
                      >
                        <div class="d-flex mt-4">
                          <div class="w-40"></div>
                          <div class="w-60">
                            <div
                              class="d-flex align-items-center justify-content-end me-24 mb-8"
                              v-for="room in row?.selectedRoom?.filter((r) =>
                                rooming.some(
                                  (sel) => (sel?.type?.value || sel?.type) === (r?.type || r?.objectType)
                                )
                              )"
                              :key="room.name"
                            >
                              <h6 class="fw-bold me-8 mb-0">{{ room.name }} :</h6>
                              <v-form-input
                                id="pricing-net-price"
                                v-model="room.price"
                                class="custom-input"
                                style="max-width: 120px"
                                autocomplete="off"
                                form-type="input-group"
                                onwheel="return false;"
                                right-addon="€"
                                step=".01"
                                type="number"
                              />
                            </div>

                            <!-- ❗ Show missing room types -->
                            <div v-if="missingRoomTypes(row)?.length > 0 && row.selectedHotel" class="mt-2 mb-2">
                              <p
                                v-for="missing in missingRoomTypes(row)"
                                :key="missing"
                                class="text-danger text-end mb-0"
                              >
                                {{ missingLabel(missing) }} is not available in this hotel.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>

              <!-- Step 5: Confirmation -->
              <div v-if="step === 5" class="row mb-4 mt-4">
                <div class="mt-4">
                  <div class="wizard-confirm-header text-center mb-20">
                    <div class="wizard-confirm-icon mb-12">
                      <span class="mi mi-check-circle" />
                    </div>
                    <h5 class="fw-bold mb-4">{{ $t("template.caseWizardReadyTitle") }}</h5>
                    <p class="text-muted small mb-0">{{ $t("template.caseWizardReadySubtitle") }}</p>
                  </div>

                  <div class="wizard-summary-card mb-20">
                    <div class="wizard-summary-row">
                      <span class="wizard-summary-label">{{ $t("clientTour.products") }}</span>
                      <span class="wizard-summary-value">{{ template?.name }}</span>
                    </div>
                    <div class="wizard-summary-row">
                      <span class="wizard-summary-label">{{ $t("general.shared.startDate") }}</span>
                      <span class="wizard-summary-value">{{ formatDate(initDate) }}</span>
                    </div>
                    <div v-if="alternativeStartDate" class="wizard-summary-row">
                      <span class="wizard-summary-label">{{ $t("general.shared.alternativeStartDate") }}</span>
                      <span class="wizard-summary-value">{{ formatDate(alternativeStartDate) }}</span>
                    </div>
                    <div class="wizard-summary-row">
                      <span class="wizard-summary-label">{{ $t("clientTour.client") }}</span>
                      <span class="wizard-summary-value">{{ clientNameDisplay }}</span>
                    </div>
                    <div class="wizard-summary-row">
                      <span class="wizard-summary-label">{{ $t("clientTour.pax") }}</span>
                      <span class="wizard-summary-value">{{ paxFields.length }}</span>
                    </div>
                  </div>

                  <label class="wizard-option-toggle mb-0" :class="{ 'is-checked': isCaseChecked }">
                    <input type="checkbox" class="d-none" v-model="isCaseChecked" />
                    <v-icon class="icon-compass me-8" />
                    {{ $t("clientTour.caseCreate") }}
                    <span class="wizard-toggle-check ms-8"
                      ><span v-if="isCaseChecked" class="mi mi-check" style="font-size: 10px"
                    /></span>
                  </label>
                </div>
              </div>
        </div><!-- /wizard-body -->
      </div><!-- /wizard-container -->
    </modal-body>
    <modal-footer class="d-flex justify-content-between align-items-center px-24 py-16 border-top bg-light">
      <v-button
        size="sm"
        variant="outline-secondary"
        class="rounded-pill px-20 fw-semibold"
        @click="prevStep"
        :disabled="step === 1"
      >
        <v-icon icon="arrow-left" size="sm" space="me-8" />
        {{ $t("general.button.previous") }}
      </v-button>

      <span class="wizard-step-counter text-muted small">{{ step }} / {{ totalSteps }}</span>

      <v-button
        size="sm"
        variant="dark"
        class="rounded-pill px-24 fw-semibold"
        @click="nextStep"
        :disabled="isNextDisabled || isLoading"
        :is-loading="isLoading"
      >
        {{ step === totalSteps ? $t("general.button.update") : $t("general.button.next") }}
        <v-icon v-if="step !== totalSteps" icon="arrow-right" size="sm" space="ms-8" />
      </v-button>
    </modal-footer>
  </v-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, Ref, watch } from "vue";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";
import vSelect from "vue-select";
import api from "@/api";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { VFormInput } from "@uniqoders/form";
import * as yup from "yup";
import { useI18n } from "vue-i18n";
import useFormValidation from "@/helpers/form";
import { useField } from "vee-validate";
import Datepicker from "@vuepic/vue-datepicker";
import { useAppStore } from "@/stores/app";
import { DateTime } from "luxon";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";

const props = defineProps({
  templateData: {
    type: Object,
    required: false,
    default: null,
  },
  initialData: {
    type: Object,
    required: true,
  },
  caseId: {
    type: Number,
    required: true,
  },
  agencySlug: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["closed", "reloadCase"]);

const step = ref(4);
const template = ref(props.templateData || {} as any);
const transferCheck = ref(props.initialData?.has_taxi || false);
const transportCheck = ref(props.initialData?.has_luggage || false);
const totalSteps = 5;
const isPax = ref(!!props.initialData?.pax?.length);
const paxFields: Ref<any[]> = ref(
  (props.initialData?.pax || [{ paxName: "", paxEmail: "", roomType: null }]).map((p: any) => ({
    ...p,
    roomType: p.room_type
      ? { value: p.room_type, label: p.room_type.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) }
      : null,
  }))
);
const highlightedDates = ref([] as Date[]);
const minDate = ref(DateTime.now());
const othersType = ref(false);

const roomTypes = [
  { id: "singleRoom", label: "supplier.singleRoom", value: "single_room" },
  { id: "doubleRoom", label: "supplier.doubleRoom", value: "double_room" },
  { id: "doubleTwinRoom", label: "supplier.doubleTwinRoom", value: "double_twin_room" },
  { id: "tripleRoom", label: "supplier.tripleRoom", value: "triple_room" },
  { id: "quadrupleRoom", label: "supplier.quadrupleRoom", value: "quadruple_room" },
  { id: "otherRoom", label: "supplier.otherRoom", value: "other_room" },
];

const { t } = useI18n();

const rooming = ref(
  props.initialData?.rooming?.map((r: any) => {
    const typeValue = typeof r.type === "object" ? r.type.value : r.type;
    const foundType = roomTypes.find((rt) => rt.value === typeValue);
    return {
      ...r,
      type: foundType ? { value: foundType.value, label: t(foundType.label) } : r.type,
    };
  }) || [{ type: "", quantity: null, pax: null }]
);

const showError = ref(false);
const isCreateRoom = ref(!!props.initialData?.rooming?.length);
const other_room_requirements = ref("");
const hotelSelections = ref({} as any);
const selectedRooms = ref({} as any);
const openIndex = ref<number | null>(null);
const isCaseChecked = ref<boolean>(true);
const isLoading = ref(false);

const authStore = useAuthStore();
const appStore = useAppStore();
const toast = useToast();
const router = useRouter();
const { agency } = storeToRefs(authStore);
const { settings } = storeToRefs(appStore);

const rules = yup.object({
  initDate: yup.string().required().nullable().label(t("general.shared.initDate")),
});

const parseInitialDate = (dateStr: string) => {
  if (!dateStr) return null;
  if (dateStr.includes("/")) {
    return DateTime.fromFormat(dateStr, "dd/MM/yyyy").toJSDate();
  }
  return DateTime.fromISO(dateStr).toJSDate();
};

const initialValues: Record<string, any> = {
  initDate: parseInitialDate(props.initialData?.start_date),
  alternativeStartDate: parseInitialDate(props.initialData?.alt_start_date),
};

const form = useFormValidation(rules, initialValues);
const { errors, toggleAccepted } = form;

const { value: initDate } = useField<Date | any>("initDate");
const { value: alternativeStartDate } = useField<Date | any>("alternativeStartDate");

const clientNameDisplay = computed(() => {
    if (!props.initialData?.client_id) return "-";
    if (typeof props.initialData.client_id === 'object') {
        const c = props.initialData.client_id;
        return `${c.name || ""} ${c.surname || ""}`.trim();
    }
    return String(props.initialData.client_id);
});

const missingRoomTypes = (row: { selectedRoom: any[] }) => {
  const availableTypes = row.selectedRoom?.map((r) => r?.type || r?.objectType);
  return rooming.value?.map((r) => (r.type?.value || r.type)).filter((type) => !availableTypes?.includes(type));
};

const missingLabel = (type: any) => {
  const found = rooming.value?.find((r) => (r.type?.value || r.type) === type);
  return found ? (found.type?.label || found.type) : type;
};

const toggleAccordion = (idx: number) => {
  openIndex.value = openIndex.value === idx ? null : idx;
};

const formatDate = (date: Date) => {
  return date ? DateTime.fromJSDate(date).toFormat("dd/MM/yyyy") : "-";
};

const formatDateForPayload = (date: Date) => {
  return date ? DateTime.fromJSDate(date).toFormat("yyyy-LL-dd") : null;
};

const openWebsite = (link: string | URL | undefined) => {
  window.open(link, "_blank");
};

const generateBookingURL = (
  hotelName: string,
  checkinDate: string,
  checkoutDate: string,
  adults: number,
  children = 0,
  rooms: number
): any => {
  const pad = (num: string | number) => num?.toString().padStart(2, "0");
  const [checkinDay, checkinMonth, checkinYear] = checkinDate.split("/");
  const [checkoutDay, checkoutMonth, checkoutYear] = checkoutDate.split("/");
  const encodedHotelName = encodeURIComponent(hotelName);

  const url =
    `https://www.booking.com/searchresults.html?` +
    `ss=${encodedHotelName}` +
    `&checkin_year=${checkinYear}` +
    `&checkin_month=${pad(checkinMonth)}` +
    `&checkin_monthday=${pad(checkinDay)}` +
    `&checkout_year=${checkoutYear}` +
    `&checkout_month=${pad(checkoutMonth)}` +
    `&checkout_monthday=${pad(checkoutDay)}` +
    `&group_adults=${adults}` +
    `&group_children=${children}` +
    `&no_rooms=${rooms}` +
    `&force_referer=`;

  window.open(url, "_blank");
};

const progressLine = computed(() => {
  return ((step.value - 1) / (totalSteps - 1)) * 100;
});

const onHotelChange = (row: any, selectedHotel: any) => {
  row.availableRooms = selectedHotel ? selectedHotel.rooms : [];
  hotelSelections.value[row.day] = selectedHotel;
  selectedRooms.value[row.day] = selectedHotel?.rooms || [];
};

const nextStep = () => {
  if (isNextDisabled.value) return;
  if (step.value < totalSteps) {
    step.value++;
  } else {
    onSubmitCase();
  }
};

const prevStep = () => {
  if (step.value > 1) {
    step.value--;
  }
};

const onSubmitCase = async () => {
  try {
    isLoading.value = true;
    const payload = {
      activity_id: template.value?.id,
      start_date: formatDateForPayload(initDate.value),
      alt_start_date: formatDateForPayload(alternativeStartDate.value),
      has_luggage: transportCheck.value,
      has_taxi: transferCheck.value,
      client_id: typeof props.initialData?.client_id === 'object' ? props.initialData.client_id.id : props.initialData?.client_id,
      pax: paxFields.value.map((pax: any) => ({
        ...pax,
        room_type: typeof pax.roomType === "object"
          ? pax.roomType?.value
          : pax.roomType,
      })),
      is_case: isCaseChecked.value,
      room_type: rooming.value?.map((room: any) => ({
        type: room.type.value || room.type,
        qty: room.quantity,
        pax: room.pax,
      })),
      hotel: hotelRows.value?.map((row) => {
        const availableTypes = row?.selectedRoom?.map((r: any) => r?.type || r?.objectType) || [];
        const matchedRooms = row?.selectedRoom?.filter((r: any) =>
          rooming?.value?.some((sel: any) => (sel?.type?.value || sel?.type) === (r?.type || r?.objectType))
        );
        const matchedRoomsWithQty = matchedRooms?.map((r: any) => {
          const roomType = r?.type || r?.objectType;
          const totalQty = rooming.value
            ?.filter((sel: any) => (sel?.type?.value || sel?.type) === roomType)
            .reduce((sum: number, sel: any) => sum + (sel?.quantity ?? 0), 0) ?? 0;
          return { ...r, qty: totalQty };
        });
        const missingRoomTypes = rooming.value
          ?.map((r) => (r.type?.value || r.type))
          ?.filter((type: any) => !availableTypes?.includes(type));
        return {
          day: row.day,
          date: row.date,
          hotel_id: row.selectedHotel?.model_id || row.selectedHotel?.id,
          rooms: matchedRoomsWithQty,
          missing_rooms: Array.isArray(matchedRooms) ? missingRoomTypes : [],
        };
      }),
    };

    await api.case.updateCaseFromWizard(agency?.value?.slug as string, props.caseId, payload);
    toast.success(t("general.shared.savedChanges"));
    emit("reloadCase");
    emit("closed");
  } catch (e: any) {
    console.error("❌ Failed to save case:", e);
    toast.error(e?.response?.data?.message || "An error occurred");
  } finally {
    toggleAccepted();
    isLoading.value = false;
  }
};

onMounted(async () => {
  if (props.initialData?.hotel) {
    props.initialData.hotel.forEach((row: any) => {
      hotelSelections.value[row.day] = { model_id: row.hotel_id };
      selectedRooms.value[row.day] = row.rooms;
    });
  }
  
  if (agency.value && template.value?.id) {
    const templatesData = await api.template.allActivity(agency.value.slug, { activity_id: template.value.id });
    if (templatesData?.length) {
        const found = templatesData[0];
        template.value = {
            ...found,
            ...template.value,
            label: found.name,
        };
    }
  }
});

const hotelRows = computed(() => {
  let start: DateTime;
  if (initDate.value instanceof Date) {
    start = DateTime.fromJSDate(initDate.value);
  } else if (DateTime.isDateTime(initDate.value)) {
    start = initDate.value;
  } else if (initDate.value) {
    start = DateTime.fromFormat(String(initDate.value), "dd/MM/yyyy");
  } else {
    return [];
  }

  const dayCount = template.value?.activityDaysCount || 0;

  return Array.from({ length: dayCount }, (_, i) => {
    const day = i + 1;
    const checkin = start.plus({ days: i });
    const checkout = checkin.plus({ days: 1 });
    const options = sortHotelOptions(template.value.hotels?.[day] || []);

    let selectedHotel = null;
    if (hotelSelections.value[day]) {
      const selection = hotelSelections.value[day];
      const targetId = selection.model_id || selection.id;
      selectedHotel = options.find((opt: any) => opt.model_id === targetId || opt.id === targetId) || selection;
    } else {
      selectedHotel = options.length ? options[0] : null;
    }

    const selectedRoom = selectedRooms.value[day] ?? selectedHotel?.rooms ?? [];

    return {
      day,
      date: checkin.toFormat("dd/MM/yyyy"),
      checkoutDate: checkout.toFormat("dd/MM/yyyy"),
      selectedHotel,
      hotelOptions: options,
      selectedRoom,
      availableRooms: [],
    };
  });
});

const sortHotelOptions = (hotels = []) => {
  return [...hotels].sort((a: any, b: any) => {
    const pa = a.priority ?? Infinity;
    const pb = b.priority ?? Infinity;
    return pa - pb;
  });
};

const togglePaxField = () => {
  isPax.value = !isPax.value;
  if (isPax.value && paxFields.value.length === 0) {
    addPaxEntry();
  }
};

const addPaxEntry = () => {
  paxFields.value.push({ paxName: "", paxEmail: "", roomType: null });
};

const removePaxEntry = (index: number) => {
  paxFields.value.splice(index, 1);
};

// Room capacity map for per-pax room type dropdown
const roomCapacityMap: Record<string, number> = {
  single_room: 1,
  double_room: 2,
  double_twin_room: 2,
  triple_room: 3,
  quadruple_room: 4,
  other_room: 0,
};

const paxRoomTypeOptions = (currentIndex: number) => {
  const usedSlots: Record<string, number> = {};
  paxFields.value.forEach((pax: any, idx: number) => {
    if (idx === currentIndex) return;
    const rt = pax.roomType;
    if (rt && typeof rt === "object" && rt.value) {
      usedSlots[rt.value] = (usedSlots[rt.value] || 0) + 1;
    } else if (rt && typeof rt === "string") {
      usedSlots[rt] = (usedSlots[rt] || 0) + 1;
    }
  });

  const totalSlotsPerType: Record<string, number> = {};
  rooming.value.forEach((room: any) => {
    const roomTypeValue = room.type?.value || room.type;
    if (!roomTypeValue) return;
    const capacity = roomCapacityMap[roomTypeValue] ?? 0;
    totalSlotsPerType[roomTypeValue] = (totalSlotsPerType[roomTypeValue] || 0) + capacity * (room.quantity || 0);
  });

  const options: { value: string; label: string }[] = [];
  const seenTypes = new Set<string>();
  rooming.value.forEach((room: any) => {
    const roomTypeValue = room.type?.value || room.type;
    if (!roomTypeValue || seenTypes.has(roomTypeValue)) return;
    seenTypes.add(roomTypeValue);

    const totalSlots = totalSlotsPerType[roomTypeValue] || 0;
    const used = usedSlots[roomTypeValue] || 0;
    const available = totalSlots - used;

    const currentPaxType = paxFields.value[currentIndex]?.roomType;
    const currentPaxValue = typeof currentPaxType === "object" ? currentPaxType?.value : currentPaxType;
    if (available > 0 || currentPaxValue === roomTypeValue) {
      const found = roomTypes.find((rt) => rt.value === roomTypeValue);
      options.push({
        value: roomTypeValue,
        label: found ? t(found.label) : roomTypeValue,
      });
    }
  });

  return options;
};

// Auto-open rooming panel with one default entry when entering step 2,
// and auto-open pax panel with at least one pax entry when entering step 3
watch(step, (newStep) => {
  if (newStep === 2) {
    if (!isCreateRoom.value) {
      isCreateRoom.value = true;
    }
    if (rooming.value.length === 0) {
      addRoomingEntry();
    }
  }
  if (newStep === 3) {
    if (!isPax.value) {
      isPax.value = true;
    }
    if (paxFields.value.length === 0) {
      addPaxEntry();
    }
  }
});

// Auto-assign default room types to pax based on rooming slots
watch(
  () => rooming.value.map((r: any) => ({ type: r.type, quantity: r.quantity, pax: r.pax })),
  () => {
    if (!isCreateRoom.value || rooming.value.length === 0) {
      paxFields.value.forEach((pax: any) => { pax.roomType = null; });
      return;
    }

    const validRoomTypes = new Set(
      rooming.value
        .map((r: any) => r.type?.value || r.type)
        .filter(Boolean)
    );

    paxFields.value.forEach((pax: any) => {
      const currentVal = typeof pax.roomType === "object" ? pax.roomType?.value : pax.roomType;
      if (currentVal && !validRoomTypes.has(currentVal)) {
        pax.roomType = null;
      }
    });

    const assigned: Record<string, number> = {};
    paxFields.value.forEach((pax: any) => {
      const rt = pax.roomType;
      const val = typeof rt === "object" ? rt?.value : rt;
      if (val) assigned[val] = (assigned[val] || 0) + 1;
    });

    const totalSlotsPerType: Record<string, number> = {};
    rooming.value.forEach((room: any) => {
      const roomTypeValue = room.type?.value || room.type;
      if (!roomTypeValue) return;
      const capacity = roomCapacityMap[roomTypeValue] ?? 0;
      totalSlotsPerType[roomTypeValue] = (totalSlotsPerType[roomTypeValue] || 0) + capacity * (room.quantity || 0);
    });

    paxFields.value.forEach((pax: any) => {
      const currentVal = typeof pax.roomType === "object" ? pax.roomType?.value : pax.roomType;
      if (currentVal) return;

      for (const [roomTypeValue, totalSlots] of Object.entries(totalSlotsPerType)) {
        const used = assigned[roomTypeValue] || 0;
        if (used < totalSlots) {
          const found = roomTypes.find((rt) => rt.value === roomTypeValue);
          pax.roomType = {
            value: roomTypeValue,
            label: found ? t(found.label) : roomTypeValue,
          };
          assigned[roomTypeValue] = used + 1;
          break;
        }
      }
    });
  },
  { deep: true }
);

const incrementDateByOneDay = (date: Date) => {
  if (!date) return new Date();
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + 1);
  return newDate;
};

const toggleRooming = () => {
  isCreateRoom.value = !isCreateRoom.value;
  if (isCreateRoom.value && rooming.value.length === 0) {
    addRoomingEntry();
  }
};

const addRoomingEntry = () => {
  rooming.value.push({ type: "", quantity: null, pax: null });
};

const isNextDisabled = computed(() => {
  if (step.value === 1) return !initDate.value;
  if (step.value === 2) {
    if (rooming.value.length === 0) return true;
    return rooming.value.some((room) => !room.type || !room.quantity || !room.pax);
  }
  if (step.value === 3) {
    const hasPax = paxFields.value.length > 0;
    const allPaxHaveRoomType = isCreateRoom.value
      ? paxFields.value.every((pax) => pax.roomType)
      : true;
    return !hasPax || !allPaxHaveRoomType || showError.value;
  }
  return false;
});

const totalRoomingPax = computed(() => {
  return rooming.value.reduce((total, room) => total + (Number(room?.pax) || 0), 0);
});

const totalQuantity = computed(() => rooming.value.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0));

watch([totalRoomingPax, () => paxFields.value.length], ([newTotal, paxCount]) => {
  showError.value = newTotal !== paxCount;
});

const handleInput = (event: any, room: any, field: string) => {
  const newValue = parseInt(event?.target?.value, 10);
  if (newValue > 0) {
    room[field] = newValue;
    handleRoomingPaxInput(room, newValue);
  } else {
    event.target.value = null;
  }
};

const handleKeydown = (event: any, room: any, field: string) => {
  if (event.key === "ArrowDown" && room[field] <= 0) {
    event.preventDefault();
  }
};

const handleRoomingPaxInput = (room: any, newPax: number) => {
  const roomTypeLabel = room.type?.label || "";
  const quantity = Number(room.quantity) || 0;
  let maxPax = 0;
  
  if (roomTypeLabel.includes("Single")) maxPax = 1 * quantity;
  else if (roomTypeLabel.includes("Double")) maxPax = 2 * quantity;
  else if (roomTypeLabel.includes("Triple")) maxPax = 3 * quantity;
  
  if (maxPax > 0 && Number(room.pax) > maxPax) {
    room.error = `Entered pax exceeds the maximum allowed ${maxPax} for ${roomTypeLabel}.`;
  } else {
    room.error = null;
  }
};

const handleRoomChange = (room: any) => {
  const label = room.type?.label || "";
  let multiplier = 0;
  if (label.includes("Single")) multiplier = 1;
  else if (label.includes("Double")) multiplier = 2;
  else if (label.includes("Triple")) multiplier = 3;
  
  if (multiplier > 0 && room.quantity) {
      room.pax = multiplier * room.quantity;
  }
};

const removeRoomingEntry = (index: number) => {
  rooming.value.splice(index, 1);
};

const getStepName = (step: number) => {
  switch (step) {
    case 1: return t("general.shared.product");
    case 2: return t("general.shared.rooms");
    case 3: return t("general.shared.pax");
    case 4: return t("general.shared.hotels");
    case 5: return t("general.shared.confirm");
    default: return "";
  }
};
</script>

<style scoped lang="scss">
@import "./wizard-styles.scss";
</style>